import React, { useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
  preset?: PresetType;
}

export type PresetType = 
  | 'today'
  | 'yesterday'
  | 'week-current'
  | 'week-previous'
  | 'month-current'
  | 'month-previous'
  | 'year-current'
  | 'year-previous'
  | 'custom';

interface DatePreset {
  label: string;
  value: PresetType;
  getRange: () => DateRange;
}

const startOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const startOfWeek = (date: Date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day;
  return startOfDay(new Date(d.setDate(diff)));
};

const endOfWeek = (date: Date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() + (6 - day);
  return endOfDay(new Date(d.setDate(diff)));
};

const startOfMonth = (date: Date) => {
  return startOfDay(new Date(date.getFullYear(), date.getMonth(), 1));
};

const endOfMonth = (date: Date) => {
  return endOfDay(new Date(date.getFullYear(), date.getMonth() + 1, 0));
};

const startOfYear = (date: Date) => {
  return startOfDay(new Date(date.getFullYear(), 0, 1));
};

const endOfYear = (date: Date) => {
  return endOfDay(new Date(date.getFullYear(), 11, 31));
};

// Preset configurations
export const getDatePresets = (): DatePreset[] => {
  const now = new Date();
  
  return [
    {
      label: 'Today',
      value: 'today',
      getRange: () => ({
        startDate: startOfDay(now),
        endDate: endOfDay(now),
      }),
    },
    {
      label: 'Yesterday',
      value: 'yesterday',
      getRange: () => {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return {
          startDate: startOfDay(yesterday),
          endDate: endOfDay(yesterday),
        };
      },
    },
    {
      label: 'Week - Current',
      value: 'week-current',
      getRange: () => ({
        startDate: startOfWeek(now),
        endDate: endOfWeek(now),
      }),
    },
    {
      label: 'Week - Previous',
      value: 'week-previous',
      getRange: () => {
        const lastWeek = new Date(now);
        lastWeek.setDate(lastWeek.getDate() - 7);
        return {
          startDate: startOfWeek(lastWeek),
          endDate: endOfWeek(lastWeek),
        };
      },
    },
    {
      label: 'Month - Current',
      value: 'month-current',
      getRange: () => ({
        startDate: startOfMonth(now),
        endDate: endOfMonth(now),
      }),
    },
    {
      label: 'Month - Previous',
      value: 'month-previous',
      getRange: () => {
        const lastMonth = new Date(now);
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        return {
          startDate: startOfMonth(lastMonth),
          endDate: endOfMonth(lastMonth),
        };
      },
    },
    {
      label: 'Year - Current',
      value: 'year-current',
      getRange: () => ({
        startDate: startOfYear(now),
        endDate: endOfYear(now),
      }),
    },
    {
      label: 'Year - Previous',
      value: 'year-previous',
      getRange: () => {
        const lastYear = new Date(now);
        lastYear.setFullYear(lastYear.getFullYear() - 1);
        return {
          startDate: startOfYear(lastYear),
          endDate: endOfYear(lastYear),
        };
      },
    },
    {
      label: 'Custom Period',
      value: 'custom',
      getRange: () => ({
        startDate: null,
        endDate: null,
      }),
    },
  ];
};

// Reusable Mini Calendar Component
interface MiniCalendarProps {
  onDateClick: (date: Date) => void;
  selectedRange: DateRange;
  currentMonth: Date;
}

const MiniCalendar: React.FC<MiniCalendarProps> = ({ onDateClick, selectedRange, currentMonth }) => {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  
  const weeks = [];
  let days: (number | null)[] = [];
  
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null);
  }
  
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
    if (days.length === 7) {
      weeks.push(days);
      days = [];
    }
  }
  
  if (days.length > 0) {
    while (days.length < 7) {
      days.push(null);
    }
    weeks.push(days);
  }
  
  const normalizeDate = (date: Date): number => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };
  
  const isDayInRange = (day: number | null): boolean => {
    if (!day || !selectedRange.startDate || !selectedRange.endDate) return false;
    
    const currentDate = normalizeDate(new Date(year, month, day));
    const start = normalizeDate(selectedRange.startDate);
    const end = normalizeDate(selectedRange.endDate);
    
    return currentDate >= start && currentDate <= end;
  };
  
  const isDaySelected = (day: number | null): boolean => {
    if (!day || !selectedRange.startDate) return false;
    const currentDate = normalizeDate(new Date(year, month, day));
    const start = normalizeDate(selectedRange.startDate);
    
    const isStart = currentDate === start;
    const isEnd = selectedRange.endDate && currentDate === normalizeDate(selectedRange.endDate);
    
    return isStart || isEnd;
  };
  
  const isToday = (day: number | null): boolean => {
    if (!day) return false;
    const today = new Date();
    return today.getDate() === day && 
           today.getMonth() === month && 
           today.getFullYear() === year;
  };
  
  return (
    <div className="flex-1">
      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
          <div key={day} className="text-center text-xs text-slate-500 font-medium py-1">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar Grid */}
      {weeks.map((week, i) => (
        <div key={i} className="grid grid-cols-7 mb-1">
          {week.map((day, j) => {
            const inRange = isDayInRange(day);
            const isSelected = isDaySelected(day);
            const isTodayDate = isToday(day);
            
            // Check if this is the first or last cell in the range for this row
            const isFirstInRow = j === 0 || !isDayInRange(week[j - 1]);
            const isLastInRow = j === 6 || !isDayInRange(week[j + 1]);
            
            return (
              <div key={j} className="relative">
                {/* Background layer for range connection */}
                {inRange && (
                  <div 
                    className="absolute inset-0 bg-slate-200"
                    style={{
                      borderTopLeftRadius: isFirstInRow ? '6px' : '0',
                      borderBottomLeftRadius: isFirstInRow ? '6px' : '0',
                      borderTopRightRadius: isLastInRow ? '6px' : '0',
                      borderBottomRightRadius: isLastInRow ? '6px' : '0',
                    }}
                  ></div>
                )}

                {/* Date content */}
                <div
                  onClick={() => day && onDateClick(new Date(year, month, day))}
                  className={`
                    h-8 flex items-center justify-center text-sm cursor-pointer relative z-10
                    ${!day ? 'invisible' : ''}
                    ${isSelected ? 'bg-black text-white font-semibold' : ''}
                    ${isTodayDate && !isSelected ? 'font-bold' : ''}
                    ${day && !inRange && !isSelected ? 'hover:bg-slate-100 rounded' : ''}
                  `}
                  style={isSelected ? {
                    borderTopLeftRadius: isFirstInRow ? '6px' : '0',
                    borderBottomLeftRadius: isFirstInRow ? '6px' : '0',
                    borderTopRightRadius: isLastInRow ? '6px' : '0',
                    borderBottomRightRadius: isLastInRow ? '6px' : '0',
                  } : undefined}
                >
                  {day}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Main DatePicker Component
interface DatePickerProps {
  isOpen: boolean;
  onClose: () => void;
  dateRange: DateRange;
  onApply: (range: DateRange) => void;
}

export const DatePicker: React.FC<DatePickerProps> = ({ 
  isOpen, 
  onClose, 
  dateRange,
  onApply 
}) => {
  const [selectedPreset, setSelectedPreset] = useState<PresetType>('today');
  const [tempRange, setTempRange] = useState<DateRange>(dateRange);
  const now = new Date();
  const [leftMonth, setLeftMonth] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const [rightMonth, setRightMonth] = useState(new Date(now.getFullYear(), now.getMonth() + 1, 1));

  const presets = getDatePresets();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const handlePresetClick = (preset: PresetType) => {
    setSelectedPreset(preset);
    
    if (preset === 'custom') {
      setTempRange({ startDate: null, endDate: null, preset: 'custom' });
    } else {
      const presetData = presets.find(p => p.value === preset);
      if (presetData) {
        const range = presetData.getRange();
        setTempRange({ ...range, preset });
      }
    }
  };

  const handleDateClick = (date: Date) => {
    const normalizedDate = startOfDay(date);
    
    if (!tempRange.startDate || (tempRange.startDate && tempRange.endDate)) {
      setTempRange({ startDate: normalizedDate, endDate: null, preset: 'custom' });
      setSelectedPreset('custom');
    } else {
      const startTime = tempRange.startDate.getTime();
      const clickedTime = normalizedDate.getTime();
      
      if (clickedTime >= startTime) {
        setTempRange({ ...tempRange, endDate: endOfDay(date), preset: 'custom' });
      } else {
        setTempRange({ 
          startDate: normalizedDate, 
          endDate: endOfDay(new Date(tempRange.startDate)),
          preset: 'custom'
        });
      }
    }
  };

  const handleApply = () => {
    if (tempRange.startDate && tempRange.endDate) {
      onApply(tempRange);
      onClose();
    }
  };

  const prevMonth = () => {
    setLeftMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() - 1, 1));
    setRightMonth(new Date(rightMonth.getFullYear(), rightMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setLeftMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1));
    setRightMonth(new Date(rightMonth.getFullYear(), rightMonth.getMonth() + 1, 1));
  };

  if (!isOpen) return null;

  return (
    <div className="absolute right-0 top-[calc(100%+4px)] bg-white rounded-xl shadow-2xl z-50 border border-slate-200">
      <div className="flex">
        {/* CALENDARS - Column 1 and 2 */}
        <div className="flex flex-col p-4 border-r border-slate-200" style={{ width: '560px' }}>
          {/* Navigation Header with Month/Year in center */}
          <div className="flex items-center justify-between mb-4 px-2">
            <div className="flex items-center gap-2">
              <button onClick={prevMonth} className="p-1 hover:bg-slate-100 rounded">
                <ChevronLeftIcon fontSize="small" />
              </button>
              <div className="w-36 text-center font-bold text-sm text-slate-700">
                {monthNames[leftMonth.getMonth()]} {leftMonth.getFullYear()}
              </div>
            </div>

            <div />

            <div className="flex items-center gap-2">
              <div className="w-36 text-center font-bold text-sm text-slate-700">
                {monthNames[rightMonth.getMonth()]} {rightMonth.getFullYear()}
              </div>
              <button onClick={nextMonth} className="p-1 hover:bg-slate-100 rounded">
                <ChevronRightIcon fontSize="small" />
              </button>
            </div>
          </div>

          {/* Two Calendars Side by Side */}
          <div className="flex gap-4">
            <MiniCalendar
              onDateClick={handleDateClick}
              selectedRange={tempRange}
              currentMonth={leftMonth}
            />
            <MiniCalendar
              onDateClick={handleDateClick}
              selectedRange={tempRange}
              currentMonth={rightMonth}
            />
          </div>
        </div>

        {/* PRESETS - Column 3 */}
        <div className="flex flex-col p-4" style={{ width: '200px' }}>
          <div className="flex-1 flex flex-col gap-0">
            {presets.map((preset) => (
              <button
                key={preset.value}
                onClick={() => handlePresetClick(preset.value)}
                className={`w-full text-left px-3 py-1 text-sm transition-colors duration-150 focus:outline-none ${selectedPreset === preset.value ? 'font-semibold text-slate-900' : 'text-slate-700 hover:text-slate-900'}`}
                style={{ background: 'transparent', border: 'none' }}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* APPLY BUTTON AT BOTTOM */}
          <div className="pt-3 mt-3 border-t border-slate-200">
            <button
              onClick={handleApply}
              disabled={!tempRange.startDate || !tempRange.endDate}
              className={`
                w-full px-6 py-2 rounded-md text-sm font-medium
                transition-colors duration-150
                ${tempRange.startDate && tempRange.endDate
                  ? 'bg-black text-white hover:bg-gray-800'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }
              `}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatePicker;